import {Component, EventEmitter, forwardRef, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    NgModel,
    ValidationErrors,
    Validator
} from '@angular/forms';
import moment from 'moment';
import {CustomDateAdapter} from '@core/adapter/DatePickerModule';
import {DateAdapter} from '@angular/material/core';

@Component({
    selector: 'creditsnap-date',
    templateUrl: './date.component.html',
    styleUrls: ['./date.component.scss'],
    encapsulation: ViewEncapsulation.None,
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DateComponent),
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => DateComponent),
        multi: true
    }]
})
export class DateComponent implements ControlValueAccessor, Validator, OnInit, OnChanges {
    value: Date | string;
    dateOfBirth: Date;
    @Input() id: string;
    @Input() name: string;
    @Input() label: string = 'Date of Birth (mm/dd/yyyy)';
    @Input() disabled: boolean = false;
    @Input() readonly: boolean = false;
    @Input() maxDate: Date;
    @Input() minDate: Date;
    @Input() optional: boolean = false;
    @Input() hint: string;
    @Input('data-test-id') testId: string = 'date-input';
    @Output() valueChange: EventEmitter<Date> = new EventEmitter<Date>();
    @ViewChild('csDate') dateEleRef: NgModel | undefined;
    age: number = 0;

    @Input() allowedString: string;

    constructor(private dateAdapter: DateAdapter<CustomDateAdapter>) {
    }

    validate(control: AbstractControl): ValidationErrors | null {
        if (this.optional) {
            return null;
        }
        return this.dateEleRef?.errors;
    }

    ngOnInit() {
        (this.dateAdapter as unknown as CustomDateAdapter).setAllowedString(this.allowedString);

        // if string convert to date object
        if (this.value) {
            this.dateOfBirth = new Date(this.value);
        }
        if (!this.maxDate && !this.allowedString) {
            this.maxDate = new Date();
        }
        if (this.allowedString) {
            this.maxDate = new Date();
            this.maxDate.setFullYear(this.maxDate.getFullYear() + 9999);

        }
        this.age = moment().diff(this.maxDate, 'years');

        if (!this.minDate) {
            this.minDate = new Date();
            this.minDate.setFullYear(this.minDate.getFullYear() - 100);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.minDate && changes.minDate.currentValue) {
            this.minDate = changes.minDate.currentValue;
        }
        if (changes.maxDate && changes.maxDate.currentValue) {
            this.maxDate = changes.maxDate.currentValue;
        }
        if (changes.optional && changes.optional.currentValue) {
            this.optional = changes.optional.currentValue;
        }
    }

    /**
     * Invoked when the model has been changed
     */
    onChange(_: Date | string) {
        console.log(_);
    }

    /**
     * Invoked when the model has been touched
     */
    onTouched: () => void = () => {
    }

    /**
     * Method that is invoked on an update of a model.
     */
    updateChanges() {
        this.onChange(this.value);
    }

    ///////////////
    // OVERRIDES //
    ///////////////

    /**
     * Writes a new item to the element.
     * @param value the value
     */
    writeValue(value: Date | string): void {
        // If the value is the placeholder '99/99/9999', we set it as a valid string, not a date.
        if (value === this.allowedString) {
            this.value = this.allowedString; // Store the placeholder string
            this.dateOfBirth = null; // We don't want a Date object, we want to keep it as a string
        } else {
            // Handle other cases where the value is a valid date or a string
            this.value = value;
            this.dateOfBirth = value ? new Date(value) : null;
        }

        setTimeout(() => {
            this.updateChanges();
        }, 100);
    }

    /**
     * Registers a callback function that should be called when the control's value changes in the UI.
     * @param fn
     */
    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    /**
     * Registers a callback function that should be called when the control receives a blur event.
     * @param fn
     */
    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    onChangeDate(event) {
        this.value = event.value;

        // If the user entered '99/99/9999', treat it as a valid placeholder and don't clear the field
        if (this.value === this.allowedString) {
            this.dateOfBirth = null;  // Don't treat as a valid date, keep as a placeholder
        } else if (this.value) {
            this.dateOfBirth = new Date(this.value);
        }

        setTimeout(() => {
            this.updateChanges();
            this.valueChange.emit(this.dateOfBirth);  // Emit the updated value
        }, 100);
    }

    touch() {
        this.dateEleRef.control.markAsTouched();
    }

}
